/**
 * Parses a quantity-like value into an integer with a fallback.
 * @param {string|number|null} value - Raw quantity value.
 * @param {number} [fallback=0] - Value used when parsing fails.
 * @returns {number} Parsed quantity.
 */
export function parseQuantity(value, fallback = 0) {
    const parsedValue = Number.parseInt(value, 10);
    return Number.isNaN(parsedValue) ? fallback : parsedValue;
}

/**
 * Binds quantity increase and decrease buttons inside a root element.
 * @param {HTMLElement} root - Container with quantity buttons.
 * @param {Object} [options={}] - Binding options.
 * @param {string} [options.buttonSelector='.js-quantity-decrease, .js-quantity-increase'] - Selector for all quantity buttons.
 * @param {string} [options.decreaseSelector='.js-quantity-decrease'] - Selector identifying decrease buttons.
 * @param {Function} [options.onAdjust] - Callback receiving the clicked button, event, and delta.
 * @returns {HTMLElement[]} Bound buttons.
 */
export function bindQuantityButtons(root, { buttonSelector = '.js-quantity-decrease, .js-quantity-increase', decreaseSelector = '.js-quantity-decrease', onAdjust } = {}) {
    const buttons = [...root.querySelectorAll(buttonSelector)];

    buttons.forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();

            if (typeof onAdjust === 'function') {
                onAdjust({
                    button,
                    event,
                    delta: button.matches(decreaseSelector) ? -1 : 1
                });
            }
        });
    });

    return buttons;
}

/**
 * Enables or disables quantity buttons inside a root element.
 * @param {HTMLElement} root - Container with quantity buttons.
 * @param {string} disableOrEnable - Expected values are `disable` or `enable`.
 * @param {Object} [options={}] - Toggle options.
 * @param {string} [options.buttonSelector='.js-quantity-decrease, .js-quantity-increase'] - Selector for all quantity buttons.
 */
export function toggleQuantityButtons(root, disableOrEnable, { buttonSelector = '.js-quantity-decrease, .js-quantity-increase' } = {}) {
    [...root.querySelectorAll(buttonSelector)].forEach((button) => {
        if (disableOrEnable === 'disable') {
            button.setAttribute('disabled', true);
        } else {
            button.removeAttribute('disabled');
        }
    });
}
